import i18Next from 'i18next';

export const EMAIL_REGEXP = /^[\w-]([.\w-])*@([\w-]+\.)+[A-Za-z]{2,6}$/;
export const PHONE_REGEXP = /^\+?[67][0-9]{8,8}$/;

export const PHONE_REGEXP_NOT_ALLOWED = /^(6688|6686).*$/;

export const mobilePhoneNumberValid = (phone: string) =>
  PHONE_REGEXP.test(phone) && !PHONE_REGEXP_NOT_ALLOWED.test(phone);

// Regex provided by BE to check if GA cookies are valid
export const BE_GA_REGEX = /^GA\d\.\d+\.\d+\.\d+$/;
export const BE_GA_MEASUREMENT_REGEX =
  /^GS\d\.\d+\.s\d+\$o\d+\$g\d+\$t\d+\$j\d+\$l\d+\$h\d+(\$d[a-zA-Z\d]{34})?$/;

type ValidationFunc = (value: any) => string | undefined;

// Runs multiple validators, concatenating all error messages with '-divider-' as separator
export function applyAll(...validations: Array<ValidationFunc>) {
  return (value: any) => {
    const errors = validations.reduce((acc, cur) => {
      const err = cur(value);
      if (err) {
        return err + '-divider-' + acc;
      }
      return acc;
    }, '');

    return errors.length > 0 ? errors : undefined;
  };
}

// Treats false booleans, null/undefined, the string "null", 0-length strings, and 0 as non-empty
export const isEmpty = (
  value: string | null | undefined | number | boolean,
): boolean => {
  if (typeof value === 'boolean') return !value;
  if (value == null) return true;
  // String "null" from coercion and empty strings are considered empty
  const str = String(value);
  return str === 'null' || str.length === 0;
};

export const weakPassword = (
  value: string | null | undefined,
): string | undefined => {
  // Password min length 8 chars, must contain at least 1 number or special char, 1 Uppercase and 1 Lowercase
  const STRONG_PASS_REGEX =
    /^(?!.*([A-Za-z0-9])\1{2})(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9!@#$%^&*])(?=.{8,})/;

  if (value && !STRONG_PASS_REGEX.test(value)) {
    return i18Next.t('error:validation.weak_password');
  }

  return;
};

export const hasSpaces = (
  value: string | null | undefined,
): string | undefined => {
  // Password must not contain any kind of whitespaces
  const HAS_SPACES_REGEX = /\s/;

  if (value && HAS_SPACES_REGEX.test(value)) {
    return i18Next.t('error:validation.password_contains_space');
  }

  return;
};

export const empty = (value: string | null | undefined | boolean) => {
  if (isEmpty(value)) {
    return i18Next.t('error:validation.empty_password');
  }

  return;
};

export const notEmail = (
  value: string | null | undefined,
): string | undefined => {
  if (!EMAIL_REGEXP.test(String(value))) {
    return i18Next.t('error:validation.not_email');
  }

  return;
};

// Validates Spanish DNI (8 digits + letter) and NIE (X/Y/Z prefix) using modulo-23 check
export const validateDNI = (value: string) => {
  let emailError = false;
  if (notEmail(value)) {
    emailError = true;
  }
  const validChars = 'TRWAGMYFPDXBNJZSQVHLCKET';
  const dniRexp = /^(?:\d{8}|[XYZ]\d{7})[A-Z]$/;
  const str = value.toString().toUpperCase();

  if (!dniRexp.test(str) && emailError)
    return i18Next.t('error:validation.not_email_nor_dni');

  // Normalize NIE: replace leading X/Y/Z with 0/1/2 for numeric calculation
  const nie = str
    .replace(/^[X]/, '0')
    .replace(/^[Y]/, '1')
    .replace(/^[Z]/, '2');

  const letter = str.substr(-1);
  // Validate control letter via modulo-23 index into the valid chars table
  const charIndex = parseInt(nie.substr(0, 8)) % 23;

  if (validChars.charAt(charIndex) === letter) {
    return;
  }
};

export const samePassword = (
  password: string | undefined,
  repeatPassword: string | undefined,
) => {
  if (
    !password?.length ||
    !repeatPassword?.length ||
    password === repeatPassword
  )
    return;
  return i18Next.t('error:validation.new_passwords_is_different');
};
